/**
 * A labelled value with a Copy button (and optionally Open), for the Dev
 * Dashboard values and the install link. Long URLs wrap (`break-all`): nothing
 * here may scroll sideways, in any language.
 *
 * "Copied" follows the REAL clipboard outcome (DESIGN.md §13.10): a rejected
 * write (denied permission, insecure context) shows an error instead.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Check, Copy, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

async function copyToClipboard(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

const COPIED_RESET_MS = 2_000;

interface CopyValueProps {
  label: string;
  value: string;
  /** Render the value as an "Open" link too (the install link). */
  openable?: boolean;
  /** Extra line under the value. */
  hint?: ReactNode;
  /** A small badge beside the label (the Customizer's New). Omitted: the markup is unchanged. */
  badge?: ReactNode;
}

export function CopyValue({ label, value, openable = false, hint, badge }: CopyValueProps) {
  const { t } = useTranslation('settings');
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (resetTimer.current !== null) window.clearTimeout(resetTimer.current);
    },
    [],
  );

  const onCopy = async () => {
    const ok = await copyToClipboard(value);
    if (!ok) {
      toast.error(t('shopifyConnection.store.copy.failedTitle', { label }), {
        description: t('shopifyConnection.store.copy.failedBody'),
      });
      return;
    }
    setCopied(true);
    if (resetTimer.current !== null) window.clearTimeout(resetTimer.current);
    resetTimer.current = window.setTimeout(() => setCopied(false), COPIED_RESET_MS);
  };

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {badge ? (
          <span className="flex items-center gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">{label}</span>
            {badge}
          </span>
        ) : (
          <span className="text-xs font-medium text-muted-foreground">{label}</span>
        )}
        <div className="flex flex-wrap items-center gap-2">
          {openable && (
            <Button asChild type="button" variant="outline" size="sm" className="h-7 gap-1.5 text-xs">
              <a href={value} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                {t('shopifyConnection.store.copy.open')}
              </a>
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 gap-1.5 text-xs"
            onClick={() => void onCopy()}
            aria-label={t('shopifyConnection.store.copy.copyLabel', { label })}
          >
            {copied ? (
              <Check className="h-3.5 w-3.5" aria-hidden="true" />
            ) : (
              <Copy className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            {copied ? t('shopifyConnection.store.copy.copied') : t('shopifyConnection.store.copy.copy')}
          </Button>
        </div>
      </div>
      <code className="block break-all rounded-md bg-muted/40 px-2.5 py-1.5 font-mono text-xs text-foreground ring-1 ring-border">
        {value}
      </code>
      {hint}
      <span role="status" aria-live="polite" className="sr-only">
        {copied ? t('shopifyConnection.store.copy.announce', { label }) : ''}
      </span>
    </div>
  );
}
